import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { PlusCircle, Pencil, Trash2, UserCircle, ArrowLeft, ExternalLink } from 'lucide-react'
import { getClients, deleteClient } from '../api/clients'
import { useAuth } from '../context/AuthContext'
import styles from './ClientsList.module.css'

export default function ClientsList() {
  const { token, user, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const [clients, setClients] = useState([])
  const [loading, setLoading] = useState(true)
  const [deleting, setDeleting] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!authLoading && (!user || user.role !== 'admin')) {
      navigate('/login', { replace: true })
    }
  }, [user, authLoading, navigate])

  useEffect(() => {
    if (!token) return
    getClients(token)
      .then(setClients)
      .catch(() => setError('Failed to load clients.'))
      .finally(() => setLoading(false))
  }, [token])

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete client "${name}"? This cannot be undone.`)) return
    setDeleting(id)
    try {
      await deleteClient(token, id)
      setClients((prev) => prev.filter((c) => c.id !== id))
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete client.')
    } finally {
      setDeleting(null)
    }
  }

  if (authLoading || !user) return null

  return (
    <main className={styles.page}>
      <Link to="/dashboard" className={styles.back}>
        <ArrowLeft size={16} />
        Back to Dashboard
      </Link>

      <div className={styles.header}>
        <div>
          <h1 className={styles.heading}>Clients</h1>
          <p className={styles.subtitle}>Manage your client list</p>
        </div>
        <Link to="/clients/new" className={styles.newBtn}>
          <PlusCircle size={16} />
          New Client
        </Link>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {loading ? (
        <p className={styles.loading}>Loading…</p>
      ) : clients.length === 0 ? (
        <div className={styles.empty}>
          <p>No clients yet.</p>
          <Link to="/clients/new" className={styles.emptyLink}>
            Add the first client →
          </Link>
        </div>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.avatarCell}></th>
                <th>Name</th>
                <th>Email</th>
                <th>Contact Number</th>
                <th>Website</th>
                <th>Added</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => (
                <tr key={c.id}>
                  <td className={styles.avatarCell}>
                    {c.thumbnail ? (
                      <img src={c.thumbnail} alt={c.name} className={styles.avatar} />
                    ) : (
                      <span className={styles.avatarPlaceholder}>
                        <UserCircle size={20} />
                      </span>
                    )}
                  </td>
                  <td className={styles.name}>{c.name}</td>
                  <td className={styles.muted}>{c.email || '—'}</td>
                  <td className={styles.muted}>{c.contact_number || '—'}</td>
                  <td className={styles.muted}>
                    {c.website_address ? (
                      <a
                        href={c.website_address}
                        target="_blank"
                        rel="noreferrer"
                        className={styles.websiteLink}
                      >
                        <ExternalLink size={13} />
                        {new URL(c.website_address).hostname}
                      </a>
                    ) : '—'}
                  </td>
                  <td className={styles.muted}>
                    {c.created_at ? new Date(c.created_at).toLocaleDateString() : '—'}
                  </td>
                  <td>
                    <div className={styles.rowActions}>
                      <Link
                        to={`/clients/${c.id}/edit`}
                        className={styles.iconBtn}
                        title="Edit client"
                      >
                        <Pencil size={14} />
                      </Link>
                      <button
                        className={`${styles.iconBtn} ${styles.deleteBtn}`}
                        onClick={() => handleDelete(c.id, c.name)}
                        disabled={deleting === c.id}
                        title="Delete client"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </main>
  )
}
